import { Box } from '@chakra-ui/react';
import { forwardRef, useImperativeHandle, useEffect, useMemo } from 'react';
import { FormBuilder } from '@/components/formBuilder';
import { useOfferStore } from '../store';

interface OfferDetailsProps {
  initialValues: any;
  detailsFormConfig: any[];
  detailsFormRef: any;
  // Token names of missing re-approval fields to flag with an info icon (skip flow).
  reapprovalHighlightTokens?: string[];
}

export const OfferDetails = forwardRef<any, OfferDetailsProps>(
  function OfferDetails(props, comRef) {
    const {
      initialValues = {},
      detailsFormConfig = [],
      detailsFormRef: formRef,
      reapprovalHighlightTokens = [],
    } = props;
    const setOfferPayload = useOfferStore((state) => state.setOfferPayload);

    // Flag only the passed-in re-approval fields with an info-icon + tooltip (via labelInfoText).
    const config = useMemo(() => {
      if (!reapprovalHighlightTokens?.length) return detailsFormConfig;
      const highlight = new Set(reapprovalHighlightTokens);
      const decorate = (field: any) =>
        field?.tokenName && highlight.has(field.tokenName)
          ? {
              ...field,
              labelInfoText:
                'This field is mandatory to use the selected offer template',
              labelInfoIconColor: 'error-200',
            }
          : field;
      return detailsFormConfig.map((section: any) =>
        section?.fields
          ? { ...section, fields: section.fields.map(decorate) }
          : decorate(section)
      );
    }, [detailsFormConfig, reapprovalHighlightTokens]);

    // Seed the store on mount so step 2 can read filled values even when the
    // user never clicked "Next" (e.g. edit flow that starts on step 2).
    useEffect(() => {
      if (initialValues && Object.keys(initialValues).length > 0) {
        setOfferPayload(initialValues);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // First flagged re-approval field in DOM order, to scroll into view on open.
    const firstHighlightName = useMemo(() => {
      if (!reapprovalHighlightTokens?.length) return null;
      const highlight = new Set(reapprovalHighlightTokens);
      for (const section of detailsFormConfig) {
        const fields = section?.fields ?? [section];
        const match = fields.find(
          (f: any) => f?.tokenName && highlight.has(f.tokenName)
        );
        if (match?.name) return String(match.name);
      }
      return null;
    }, [detailsFormConfig, reapprovalHighlightTokens]);

    // Scroll to the first flagged field on open; anchor on the label (htmlFor={name})
    // since not all inputs set id, and retry a few times while the drawer/FormBuilder remounts.
    useEffect(() => {
      if (!firstHighlightName) return;
      const escaped = window.CSS?.escape
        ? window.CSS.escape(firstHighlightName)
        : firstHighlightName;
      let attempts = 0;
      let timer: ReturnType<typeof setTimeout>;
      const tryScroll = () => {
        const el =
          document.getElementById(firstHighlightName) ||
          document.querySelector(`label[for="${escaped}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        if (attempts++ < 10) timer = setTimeout(tryScroll, 100);
      };
      timer = setTimeout(tryScroll, 150);
      return () => clearTimeout(timer);
    }, [firstHighlightName]);

    useImperativeHandle(comRef, () => ({
      getModel: async () => {
        const formValues = formRef?.current?.values || {};
        const validationErrors = (await formRef?.current?.validateForm()) || {};
        const isValid = Object.keys(validationErrors).length === 0;
        if (!isValid) {
          await formRef?.current?.submitForm();
          await new Promise((resolve) => setTimeout(resolve, 50));
          const customFieldsErrArray = Object.keys(
            formRef?.current?.errors || {}
          );
          const invalidElements = document.querySelectorAll(
            '[aria-invalid="true"]'
          );

          if (invalidElements?.length > 0) {
            const errorElement = invalidElements[0];
            errorElement?.scrollIntoView?.({
              behavior: 'smooth',
              block: 'center',
            });
            return;
          }

          if (customFieldsErrArray?.length > 0) {
            const element = document?.getElementById?.(
              customFieldsErrArray?.[0]
            );
            element?.scrollIntoView?.({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }
        if (isValid) {
          setOfferPayload(formValues);
        }
        return {
          hasValidData: isValid,
          payload: formValues,
        };
      },
    }));

    const handleDetailsFormSubmit = (values = {}) => {
      console.log('failed_modal_values', values);
    };

    return (
      <Box ref={comRef}>
        <Box h="100%" w="100%" overflowY="auto" overflowX="hidden" p="0">
          <FormBuilder
            key={
              [
                'type_of_equity',
                'amount',
                'cliff_of_vesting',
                'cycles_after_cliff',
                'period_of_vesting',
                'strike_price',
              ].some(
                (k) =>
                  initialValues?.[k] !== null &&
                  initialValues?.[k] !== undefined
              )
                ? 'ready'
                : 'pending'
            }
            initialValues={initialValues || {}}
            config={config}
            onSubmit={handleDetailsFormSubmit}
            formRef={formRef}
          />
        </Box>
      </Box>
    );
  }
);

OfferDetails.displayName = 'OfferDetails';
